/** Magazyn — normy i wykonanie (Etap 6): teksty pod `wh.*`. */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const whPl = {
  nav: 'Magazyn — normy',
  title: 'Magazyn — normy i wykonanie',
  description: 'Wykonanie norm kompletacji per zmiana, zespół i osoba, normatywy z historii z korektą ręczną, analiza pod premie.',
  demo: {
    badge: 'dane przykładowe',
    title: 'Dane przykładowe — symulacja kompletacji',
    body: 'InsERT nie zapisuje lokalizacji ani czasów kompletacji, a WMS jeszcze nie działa. Prawdziwe są dokumenty WZ z ERP (liczba pozycji, ilość, godzina utworzenia); strefy, magazynierzy, zespoły i czasy kompletacji to symulacja. Gdy ruszy WMS lub terminale, zadania przyjdą z importu CSV i ta informacja zniknie.',
  },
  hero: { performance: 'Wykonanie normy', linesPerHour: 'Pozycji / h', tasks: 'Zadania', documents: 'WZ z ERP' },
  tabs: { performance: 'Wykonanie', tasks: 'Zadania', norms: 'Normy', bonus: 'Premie', layout: 'Strefy i zespoły', import: 'Import z WMS', settings: 'Ustawienia' },
  range: { from: 'Od', to: 'Do', last7: '7 dni', last30: '30 dni', thisMonth: 'Ten miesiąc' },
  groupBy: { person: 'Osoby', team: 'Zespoły', shift: 'Zmiany', zone: 'Strefy', day: 'Dni' },
  col: {
    name: 'Nazwa',
    tasks: 'Zadania',
    lines: 'Pozycje',
    actualHours: 'Godz. rzecz.',
    normHours: 'Godz. norm.',
    linesPerHour: 'Poz. / h',
    performance: 'Wykonanie',
    withoutNorm: 'Bez normy',
    date: 'Doba',
    shift: 'Zmiana',
    document: 'Dokument',
    zone: 'Strefa',
    kind: 'Rodzaj',
    picker: 'Kompletujący',
    start: 'Start',
    minutes: 'Min',
    aisles: 'Alejki',
    norm: 'Norma',
  },
  kpi: {
    performance: 'Wykonanie normy',
    performanceHint: 'Godziny normatywne ÷ godziny rzeczywiste (zadania z normą)',
    linesPerHour: 'Pozycji na godzinę',
    tasks: 'Zadania kompletacji',
    tasksHint: '{lines} pozycji · {hours} h',
    documents: 'Dokumenty WZ z ERP',
    documentsHint: '{withTasks} z zadaniami kompletacji',
    lastImport: 'Ostatni import WZ z ERP: {at}',
    never: 'jeszcze nie było',
    byZone: 'Strefy',
    byShift: 'Zmiany',
    trend: 'Wykonanie dzień po dniu',
    linesPerHourHint: 'Tempo z {hours} h kompletacji',
  },
  perf: {
    ranking: 'Ranking — {by}',
    clickHint: 'Kliknij wiersz, aby zobaczyć wykonanie dzień po dniu i rozbicie.',
    search: { person: 'Szukaj osoby lub zespołu…', team: 'Szukaj zespołu…', shift: 'Szukaj zmiany…', zone: 'Szukaj strefy…', day: 'Szukaj dnia…' },
    bucketsLabel: 'Próg wykonania',
    bucket: { all: 'Wszystkie', good: '≥ 100%', near: '90–100%', low: '< 90%' },
    shown: '{shown} z {total}',
    noMatch: 'Nic nie pasuje do filtra.',
    total: 'Razem',
    withoutNorm: '{n} bez normy',
    missing: 'konto usunięte',
    missingHint: 'Tej osoby nie ma już w systemie (konto zostało usunięte). Jej zadania zostały w historii, więc liczą się do wykonania strefy, zmiany i magazynu.',
    inactive: 'nieaktywne',
    noTeam: 'bez zespołu',
    seriesPerf: 'Wykonanie normy',
    seriesLph: 'Pozycji / h',
    normLine: 'norma 100%',
    leaders: 'Rozkład i liderzy',
    leadersHint: '{n} osób z normą w okresie',
    top: 'Najlepsi',
    bottom: 'Do wsparcia',
    period: 'Okres: {from} – {to}',
    linesSub: '{n} poz.',
    normSub: 'norma: {n} h',
    byZone: 'Wykonanie wg stref',
    byPerson: 'Wykonanie wg osób',
    close: 'Zamknij',
  },
  kind: { SMALL: 'Małe (do 5 poz.)', MEDIUM: 'Średnie (6–20 poz.)', LARGE: 'Duże (ponad 20 poz.)' },
  source: { SIMULATION: 'Symulacja', WMS_CSV: 'WMS (CSV)' },
  tasks: { empty: 'Brak zadań kompletacji w tym okresie.', page: 'Strona {page} z {pages} · {total} zadań', prev: 'Poprzednia', next: 'Następna', all: 'Wszystkie' },
  norms: {
    title: 'Normy kompletacji (pozycji na godzinę)',
    hint: 'Normatyw = {method} wydajności zadań z ostatnich {days} dni (min. {sample} zadań). Korekta ręczna ma pierwszeństwo i przechodzi na kolejne przeliczenia.',
    recompute: 'Przelicz z historii',
    recomputed: 'Przeliczono normy obowiązujące od {date}.',
    validFrom: 'Obowiązuje od',
    computed: 'Wyliczona',
    manual: 'Korekta',
    rate: 'Norma',
    sample: 'Próbka',
    none: 'za mała próbka',
    edit: 'Koryguj',
    dialogTitle: 'Korekta normy — {zone}, {kind}',
    manualRate: 'Norma ręczna (pozycji / h)',
    note: 'Uzasadnienie',
    clear: 'Przywróć wyliczoną',
    save: 'Zapisz korektę',
    by: '{name}, {date}',
    history: 'Pokaż poprzednie wersje',
    empty: 'Brak norm — przelicz je z historii zadań.',
  },
  method: { MEDIAN: 'mediana', P60: '60. percentyl', P75: '75. percentyl' },
  bonus: {
    title: 'Wykonanie normy w miesiącu — analiza pod premie',
    export: 'Eksport XLSX',
    tiers: 'Progi: {tiers}; minimum {hours} h kompletacji w miesiącu.',
    tier: 'Poziom',
    reason: { OK: 'Kwalifikuje się', MIN_HOURS: 'Za mało godzin', BELOW: 'Poniżej progu' },
    summary: '{eligible} z {total} osób powyżej progu',
    empty: 'Brak zadań kompletacji w tym miesiącu.',
  },
  layout: {
    zones: 'Linie / strefy kompletacji',
    teams: 'Zespoły',
    code: 'Kod',
    groups: 'Grupy towarów',
    aisles: 'Alejki',
    locations: 'Lokalizacje',
    members: 'Skład',
    leader: 'Lider',
    inactive: 'nieaktywna',
    noGroups: 'Brak przypisanych grup (trafiają tu towary bez strefy)',
    emptyZones: 'Brak stref.',
    emptyTeams: 'Brak zespołów.',
  },
  import: {
    title: 'Zdarzenia kompletacji z WMS / terminali (CSV)',
    body: 'Gdy magazyn zacznie rejestrować kompletację (WMS lub terminale), zadania wczytasz plikiem CSV. Ponowny import tego samego event_id aktualizuje zadanie. Import nie jest wymagany — bez niego moduł pokazuje dane przykładowe.',
    format: 'Format (docs/WAREHOUSE-WMS-CSV.md): event_id; document_number; zone_code; picker_id (numer ewidencyjny lub e-mail konta); started_at; finished_at; lines; quantity; aisles (oddzielone |). Separator ; lub , — czas lokalny „RRRR-MM-DD GG:MM” albo ISO 8601 ze strefą.',
    choose: 'Wybierz plik CSV',
    validate: 'Sprawdź bez zapisu',
    run: 'Importuj',
    result: 'Wierszy: {rows} · nowe: {created} · zaktualizowane: {updated} · odrzucone: {rejected}',
    dryRun: 'Próba — nic nie zapisano.',
    errors: 'Odrzucone wiersze',
    row: 'Wiersz {row}',
    failed: 'Import nie powiódł się.',
  },
  settings: {
    norms: 'Normatyw',
    normMethod: 'Metoda',
    historyDays: 'Historia (dni)',
    minSample: 'Minimalna próbka (zadań)',
    day: 'Doba i zmiany',
    dayStartHour: 'Początek doby magazynowej (godzina poprzedniego dnia)',
    shiftKey: 'Klucz',
    shiftLabel: 'Nazwa',
    shiftFrom: 'Od (h)',
    shiftTo: 'Do (h)',
    bonus: 'Premie',
    bonusMinHours: 'Minimum godzin kompletacji w miesiącu',
    tierPct: 'Próg (%)',
    tierLabel: 'Nazwa poziomu',
    addTier: 'Dodaj próg',
    remove: 'Usuń',
    save: 'Zapisz ustawienia',
    saved: 'Zapisano ustawienia magazynu.',
    invalid: 'Zmiany muszą pokrywać całą dobę bez nakładania się.',
    readOnly: 'Zmiany ustawień wymagają uprawnienia „Magazyn — zarządzanie”.',
  },
  error: 'Nie udało się wczytać danych magazynu.',
  retry: 'Spróbuj ponownie',
};

export const whEn: Shape<typeof whPl> = {
  nav: 'Warehouse — norms',
  title: 'Warehouse — norms and performance',
  description: 'Picking norm performance per shift, team and person, norms from history with manual correction, bonus analysis.',
  demo: {
    badge: 'sample data',
    title: 'Sample data — simulated picking',
    body: 'InsERT keeps no locations or picking times and there is no WMS yet. The delivery notes from the ERP are real (lines, quantity, creation time); zones, pickers, teams and picking times are simulated. Once a WMS or terminals run, tasks come from the CSV import and this notice disappears.',
  },
  hero: { performance: 'Norm performance', linesPerHour: 'Lines / h', tasks: 'Tasks', documents: 'ERP delivery notes' },
  tabs: { performance: 'Performance', tasks: 'Tasks', norms: 'Norms', bonus: 'Bonuses', layout: 'Zones & teams', import: 'WMS import', settings: 'Settings' },
  range: { from: 'From', to: 'To', last7: '7 days', last30: '30 days', thisMonth: 'This month' },
  groupBy: { person: 'People', team: 'Teams', shift: 'Shifts', zone: 'Zones', day: 'Days' },
  col: {
    name: 'Name',
    tasks: 'Tasks',
    lines: 'Lines',
    actualHours: 'Actual h',
    normHours: 'Norm h',
    linesPerHour: 'Lines / h',
    performance: 'Performance',
    withoutNorm: 'No norm',
    date: 'Day',
    shift: 'Shift',
    document: 'Document',
    zone: 'Zone',
    kind: 'Kind',
    picker: 'Picker',
    start: 'Start',
    minutes: 'Min',
    aisles: 'Aisles',
    norm: 'Norm',
  },
  kpi: {
    performance: 'Norm performance',
    performanceHint: 'Norm hours ÷ actual hours (tasks with a norm)',
    linesPerHour: 'Lines per hour',
    tasks: 'Picking tasks',
    tasksHint: '{lines} lines · {hours} h',
    documents: 'ERP delivery notes',
    documentsHint: '{withTasks} with picking tasks',
    lastImport: 'Last delivery note import from the ERP: {at}',
    never: 'not yet',
    byZone: 'Zones',
    byShift: 'Shifts',
    trend: 'Performance day by day',
    linesPerHourHint: 'Pace over {hours} h of picking',
  },
  perf: {
    ranking: 'Ranking — {by}',
    clickHint: 'Click a row to see day-by-day performance and a breakdown.',
    search: { person: 'Search person or team…', team: 'Search team…', shift: 'Search shift…', zone: 'Search zone…', day: 'Search day…' },
    bucketsLabel: 'Performance threshold',
    bucket: { all: 'All', good: '≥ 100%', near: '90–100%', low: '< 90%' },
    shown: '{shown} of {total}',
    noMatch: 'Nothing matches the filter.',
    total: 'Total',
    withoutNorm: '{n} without norm',
    missing: 'account deleted',
    missingHint: 'This person is no longer in the system (the account was deleted). Their tasks stay in history and still count towards zone, shift and warehouse performance.',
    inactive: 'inactive',
    noTeam: 'no team',
    seriesPerf: 'Norm performance',
    seriesLph: 'Lines / h',
    normLine: 'norm 100%',
    leaders: 'Distribution and leaders',
    leadersHint: '{n} people with a norm in the period',
    top: 'Top performers',
    bottom: 'Need support',
    period: 'Period: {from} – {to}',
    linesSub: '{n} lines',
    normSub: 'norm: {n} h',
    byZone: 'Performance by zone',
    byPerson: 'Performance by person',
    close: 'Close',
  },
  kind: { SMALL: 'Small (up to 5 lines)', MEDIUM: 'Medium (6–20 lines)', LARGE: 'Large (over 20 lines)' },
  source: { SIMULATION: 'Simulation', WMS_CSV: 'WMS (CSV)' },
  tasks: { empty: 'No picking tasks in this period.', page: 'Page {page} of {pages} · {total} tasks', prev: 'Previous', next: 'Next', all: 'All' },
  norms: {
    title: 'Picking norms (lines per hour)',
    hint: 'Norm = {method} of task speed over the last {days} days (min. {sample} tasks). A manual correction takes precedence and carries over to later recomputations.',
    recompute: 'Recompute from history',
    recomputed: 'Norms recomputed, valid from {date}.',
    validFrom: 'Valid from',
    computed: 'Computed',
    manual: 'Correction',
    rate: 'Norm',
    sample: 'Sample',
    none: 'sample too small',
    edit: 'Correct',
    dialogTitle: 'Norm correction — {zone}, {kind}',
    manualRate: 'Manual norm (lines / h)',
    note: 'Reason',
    clear: 'Back to computed',
    save: 'Save correction',
    by: '{name}, {date}',
    history: 'Show previous versions',
    empty: 'No norms — recompute them from the task history.',
  },
  method: { MEDIAN: 'median', P60: '60th percentile', P75: '75th percentile' },
  bonus: {
    title: 'Monthly norm performance — bonus analysis',
    export: 'Export XLSX',
    tiers: 'Tiers: {tiers}; at least {hours} h of picking in the month.',
    tier: 'Tier',
    reason: { OK: 'Qualifies', MIN_HOURS: 'Too few hours', BELOW: 'Below threshold' },
    summary: '{eligible} of {total} people above the threshold',
    empty: 'No picking tasks in this month.',
  },
  layout: {
    zones: 'Picking lines / zones',
    teams: 'Teams',
    code: 'Code',
    groups: 'Product groups',
    aisles: 'Aisles',
    locations: 'Locations',
    members: 'Members',
    leader: 'Leader',
    inactive: 'inactive',
    noGroups: 'No groups assigned (products without a zone land here)',
    emptyZones: 'No zones.',
    emptyTeams: 'No teams.',
  },
  import: {
    title: 'Picking events from a WMS / terminals (CSV)',
    body: 'Once the warehouse records picking (WMS or terminals), load the tasks from a CSV file. Re-importing the same event_id updates the task. The import is optional — without it the module shows sample data.',
    format: 'Format (docs/WAREHOUSE-WMS-CSV.md): event_id; document_number; zone_code; picker_id (employee number or account e-mail); started_at; finished_at; lines; quantity; aisles (separated by |). Separator ; or , — local time "YYYY-MM-DD HH:MM" or ISO 8601 with offset.',
    choose: 'Choose a CSV file',
    validate: 'Validate only',
    run: 'Import',
    result: 'Rows: {rows} · new: {created} · updated: {updated} · rejected: {rejected}',
    dryRun: 'Dry run — nothing saved.',
    errors: 'Rejected rows',
    row: 'Row {row}',
    failed: 'Import failed.',
  },
  settings: {
    norms: 'Norm',
    normMethod: 'Method',
    historyDays: 'History (days)',
    minSample: 'Minimum sample (tasks)',
    day: 'Day and shifts',
    dayStartHour: 'Warehouse day starts at (hour of the previous day)',
    shiftKey: 'Key',
    shiftLabel: 'Name',
    shiftFrom: 'From (h)',
    shiftTo: 'To (h)',
    bonus: 'Bonuses',
    bonusMinHours: 'Minimum picking hours in the month',
    tierPct: 'Threshold (%)',
    tierLabel: 'Tier name',
    addTier: 'Add tier',
    remove: 'Remove',
    save: 'Save settings',
    saved: 'Warehouse settings saved.',
    invalid: 'Shifts must cover the whole day without overlapping.',
    readOnly: 'Changing settings requires the "Warehouse — manage" permission.',
  },
  error: 'Could not load warehouse data.',
  retry: 'Try again',
};
